import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ProjectService } from '../../services/ProjectService/project';
import { AdminService } from '../../services/AdminService/admin';
import { CreateProjectRequest, ProjectResponse } from '../../models/project.dto';
import { CompanyResponse } from '../../models/company.dto';
import { extractErrorMessage } from '../../shared/error-utils';
import { ErrorBanner } from '../../shared/components/error-banner/error-banner';

@Component({
  selector: 'app-create-project-dialog',
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatIconModule,
    ErrorBanner,
  ],
  templateUrl: './create-project-dialog.html',
  styleUrl: './create-project-dialog.scss',
})
export class CreateProjectDialog implements OnInit {
  form: CreateProjectRequest = { name: '', description: '', registrationNo: '' };

  // Superadmin is platform-scoped and has no company of its own, so the owning company is chosen
  // here and sent as registrationNo — the same identifier AddParticipantDialog uses.
  companies = signal<CompanyResponse[]>([]);
  isLoadingCompanies = signal(false);
  owner: CompanyResponse | string = '';

  isSubmitting = signal(false);
  errorMessage = signal('');

  constructor(
    private dialogRef: MatDialogRef<CreateProjectDialog>,
    private projectService: ProjectService,
    private admin: AdminService
  ) {}

  ngOnInit(): void {
    this.isLoadingCompanies.set(true);
    this.admin.listCompanies('APPROVED', 0, 100).subscribe({
      next: (res) => {
        this.isLoadingCompanies.set(false);
        this.companies.set(res.data);
      },
      error: (err: HttpErrorResponse) => {
        this.isLoadingCompanies.set(false);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to load companies.'));
      },
    });
  }

  get filteredCompanies(): CompanyResponse[] {
    const term = typeof this.owner === 'string' ? this.owner.trim().toLowerCase() : '';
    if (!term) {
      return this.companies();
    }
    return this.companies().filter(
      (c) => c.name.toLowerCase().includes(term) || c.registrationNo.toLowerCase().includes(term)
    );
  }

  displayCompany = (company: CompanyResponse | string): string => {
    if (!company) {
      return '';
    }
    return typeof company === 'string'
      ? company
      : `${company.name} — ${company.registrationNo} (${company.countryCode})`;
  };

  get canSubmit(): boolean {
    return !!this.form.name.trim() && typeof this.owner === 'object' && !!this.owner;
  }

  cancel(): void {
    this.dialogRef.close();
  }

  submit(): void {
    if (!this.canSubmit) {
      return;
    }

    this.isSubmitting.set(true);
    this.errorMessage.set('');

    const payload: CreateProjectRequest = {
      ...this.form,
      registrationNo: (this.owner as CompanyResponse).registrationNo,
    };

    this.projectService.create(payload).subscribe({
      next: (res) => {
        this.isSubmitting.set(false);
        this.dialogRef.close(res.data as ProjectResponse);
      },
      error: (err: HttpErrorResponse) => {
        this.isSubmitting.set(false);
        this.errorMessage.set(extractErrorMessage(err, 'Failed to create project.'));
      },
    });
  }
}
